import { useEffect, useMemo, useState } from "react";
import Particles, { initParticlesEngine } from "@tsparticles/react";
import { loadSlim } from "@tsparticles/slim"; 
import { motion } from "framer-motion";
import Card from "./Components/Card";

function App() {
  const [init, setInit] = useState(false);

  useEffect(() => {
    initParticlesEngine(async (engine) => {
      await loadSlim(engine);
    }).then(() => {
      setInit(true);
    });
  }, []);

  const options = useMemo(() => {
    return {
      background: {
        color: {
          value: "#222", 
        },
      },
      fpsLimit: 120,
     interactivity: {
        events: {
          onHover: {
            enable: true, 
            mode: "attract", 
          },
        },
        modes: {
          attract: {
            distance: 200, 
            duration: 0.4, 
            speed: 1, 
          },
        grab: {
          distance: 200,  
          links: {
            opacity: 1, 
            color: "#0f5", 
          },
        },
        },
      },
      particles: {
        number: {
          value: 100,
          density: {
            enable: true,
            value_area: 800,
          },
        },
        color: {
          value: "#0f5", 
        },
        shape: {
          type: "circle",
        },
        opacity: {
          value: 0.5,
          random: true,
          anim: {
            enable: false,
          },
        },
        size: {
          value: 3,
          random: true,
        },
        links: {
          enable: true,
          distance: 150,
          color: "#fff",
          opacity: 0.4,
          width: 1,
        },
        move: {
          direction: "none",
          enable: true,
          outModes: {
            default: "bounce",
          },
          random: false,
          speed: 1,
          straight: false,
        },
      },
      detectRetina: true,
    };
  }, []);

  if (!init) {
    return <div>Loading particles...</div>; 
  }

return (
<div className="relative min-h-screen overflow-x-hidden overflow-y-scroll">
  <Particles
    id="tsparticles"
    options={options}
    className="fixed top-0 left-0 w-full h-full -z-10 pointer-events-none"
  />
  <div className="flex flex-col w-full px-4 sm:px-6 lg:px-8">
    {/* Landing Page */}
    <motion.div
      className="flex flex-col w-full py-12 sm:py-20 justify-center items-center min-h-screen relative px-4"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
    >
      <h1 className="text-2xl sm:text-4xl lg:text-[3rem] text-white text-center font-bold">Hi! I'm Ethan Rowley</h1>
      <p className="text-white mt-2 text-center text-sm sm:text-base lg:text-lg max-w-2xl">
        Software Development Engineer At Decathlon UK
      </p>
      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 w-full flex justify-center">
        <span className="sticky bottom-10 animate-bounce z-10">
          <svg width="32" height="32" className="sm:w-10 sm:h-10" viewBox="0 0 24 24" fill="none">
            <path d="M12 16l-6-6h12l-6 6z" fill="#fff"/>
          </svg>
        </span>
      </div>
    </motion.div>

    {/* Experience */}
    <motion.div
      className="flex flex-col w-full py-12 sm:py-20 justify-start items-center px-4"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
    >
      <h1 className="text-2xl sm:text-4xl lg:text-[3rem] text-white text-center font-bold mb-8">Experience</h1>
      <Card
        title="Software Engineer + DevOps at Decathlon UK"
        description="Software Engineer at Decathlon UK"
        date="2025 - Present"
        skills={["Java", "Terraform", "Spring Boot", "Postgres SQL", "React JS", "Cloud Run", "GCP", "Cloud Storage", "JavaScript", "TypeScript", "AppScript", "Automation", "Docker"]}
      />
      <Card
        title="Part Time Software Engineer at Decathlon UK"
        description="Part Time Software Engineer at Decathlon UK"
        date="2024-2025"
        skills={["Nest JS", "Github", "Git", "Java", "Java Spring"]}
      />
      <Card
        title="Software Engineer Intern at Decathlon UK"
        description="Software Engineer Intern at Decathlon UK"
        date="2023-2024"
        skills={["GCP Cloud Run", "React JS", "Java", "Spring", "GCP", "TypeScript", "Cloud Buckets", "GCP Scheduler", "Docker", "AppScript"]}
      />
      <Card
        title="Sports Advisor at Decathlon Giltbrook"
        description="Sports Advisor at Decathlon Giltbrook"
        date="2019-2023"
        skills={["Communication", "Teamwork", "Time Management"]}
      />
      <Card
        title="Innaxys 1 Week Work Placement"
        description="1 Week Work Placement at Innaxys"
        date="2018"
        skills={["JQuery", "JavaScript", "HTML", "CSS"]}
      />
    </motion.div>

    {/* Qualifications + Awards */}
    <motion.div
      className="flex flex-col w-full py-12 sm:py-20 justify-start items-center px-4"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
    >
      <h1 className="text-2xl sm:text-4xl lg:text-[3rem] text-white text-center font-bold mb-8">Qualifications + Awards</h1>
      <Card
        title="Decathlon Hackathon Q3 Winning Team"
        description="Winning team in Decathlon DPE Hackathon Q3"
        date="2025"
        skills={["Python", "FastAPI", "Postgres SQL", "Typescript", "React JS"]}
        links={[{url: "https://drive.google.com/file/d/1ZF6-dhbSf-HqjZke313uiB_1-N1sW3Xe/view?usp=sharing", label: "Certificate"}]}
      />
      <Card
        title="Computer Science With Year In Industry at University of Nottingham"
        description="Achieved a 2:1 Bachelors with Honours"
        date="2021 - 2025"
        skills={["Mathmatics for Computer Science 1 & 2", "Operating Systems & Concurrency", "Human-AI Interaction", "Games", "Algorithms, Data structures & Efficiency", "Developing Maintainable Software", "C++", "Languages and Computation", "Mobile Device Programming", "Computer Security", "Computer Fundamentals", "Programming & Algorithms"]}
        links={[{url: "https://www.nottingham.ac.uk/studywithus/ugstudy/courses/UG/Computer-Science-with-Year-in-Industry-BSc-Hons-U6UCMPSCY.html", label: "Degree Webpage"}]}
      />
    </motion.div>

    {/* Projects */}
    <motion.div
      className="flex flex-col w-full py-12 sm:py-20 justify-start items-center px-4"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
    >
      <h1 className="text-2xl sm:text-4xl lg:text-[3rem] text-white text-center font-bold mb-8">Projects</h1>
      <Card
        title="HRC Hub - Mobile App"
        description="React Native Mobile App called HRC Hub for my local running club, on IOS and Android"
        date="2023 - 2025"
        skills={["React Native", "Firebase", "Expo", "Google Play", "IOS App Store"]}
      />
      <Card
        title="Platform Agnostic Smart Device Manager (Dissertation)"
        description="Dissertation: Platform Agnostic Smart Device Manager"
        date="2024 - 2025"
        skills={["Java Spring Boot", "Firestore", "Docker", "Cloud Run"]}
        links={[{url: "https://drive.google.com/file/d/1lwdDqS3atBUF6sqrY8_iuWFMT9x4Da93/view?usp=sharing", label: "Document"}]}
      />
      <Card
        title="COMP4001 Games Coursework"
        description="Unity 3D, Survival based strategy game where the aim is to survive as long as possible by placing turrets to kill enemies and earn gold, to buy towers to collect resources and buy houses to get more villagers to expand the village."
        date="2025"
        skills={["C#", "Unity 3D", "Games"]}
        links={[{url: "https://www.youtube.com/watch?v=HYWDmEyiOpU", label: "Demo Video"}]}
      />
     <Card
        title="Space Invaders Clone"
        description="Used C++ and SFML to create a clone of the game space invaders where the player controls a spaceship and can move left and right to dodge enemy bullets and is able shoot back"
        date="2023"
        skills={["C++", "SFML", "Github"]}
        links={[{url: "https://github.com/EthanRowley/SFML_SpaceInvaders/tree/main", label: "Code"}]}
      />
      <Card
        title="Bubble Sort Visualisation"
        description="C++ to visualise bubble sort"
        date="2023"
        skills={["C++", "SFML", "Github", "Bubble Sort", "Algorithms", "Visualisation"]}
        links={[{url: "https://github.com/EthanRowley/SFML_BubbleSortVisualisation", label: "Code"}]}
      />
      
    </motion.div>


    {/* Contact */}
    <motion.div
      className="flex flex-col w-full py-12 sm:py-20 justify-start items-center px-4"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
    >
      <h1 className="text-2xl sm:text-4xl lg:text-[3rem] text-white text-center font-bold mb-8">Contact</h1>
    
    </motion.div>
  </div>
</div>

);
}

export default App;